// @flow

import * as React from "react";
import cx from "classnames";
import SVG from "react-inlinesvg";

import Tooltip from "../../Tooltip";
import TooltipContent from "../../TooltipContent";

import styles from "./GenericTile.scss";

import type { TextType } from "../../TooltipContent/TooltipContent";
import type { SelectionMode } from "../../TileList/shared/TileListLogic";

export type TileClassNames = {
  button: string,
  buttonActive?: string,
  svgWrapper: string
};

type GenericTileProps = {
  name: string,
  id: string,
  src: string,
  alt: string,
  generalStyles: TileClassNames,
  specificStyles?: TileClassNames,
  isActive: boolean,
  onClick: () => void,
  selectionMode: SelectionMode,
  label: string,
  tooltipContent: Array<TextType>,
  areHoverStylesEnabled: boolean
};

const GenericTile = ({
  name,
  id,
  src,
  alt,
  generalStyles,
  specificStyles,
  selectionMode,
  isActive,
  onClick,
  label,
  tooltipContent,
  areHoverStylesEnabled,
  ...restProps
}: GenericTileProps) => {
  const buttonRef = React.useRef(null);
  const combinedStyles = [styles, generalStyles, specificStyles];

  const onButtonClick = () => {
    const buttonElement = buttonRef.current;

    if (isActive && buttonElement) {
      buttonElement.blur();
    }

    onClick();
  };

  return (
    <div className={styles.buttonWrapper}>
      <div className={styles.tooltip}>
        {tooltipContent && tooltipContent.length > 0 && (
          <Tooltip
            content={() => <TooltipContent texts={tooltipContent} />}
            type={isActive ? "activeTile" : "inactiveTile"}
          />
        )}
      </div>
      <button
        ref={buttonRef}
        type="button"
        className={cx(
          ...combinedStyles.map(c => c.button),
          ...combinedStyles.map(c => ({ [c.buttonActive]: isActive })),
          {
            [styles.enableHoverStyles]: areHoverStylesEnabled
          }
        )}
        onClick={onButtonClick}
        data-isactive={isActive}
        data-testid={`tileButton:${id}`}
        data-name={name}
        role={selectionMode === "single" ? "radio" : "checkbox"}
        aria-checked={isActive}
        aria-label={label}
        {...restProps}
      >
        <SVG className={cx(...combinedStyles.map(c => c.svgWrapper))} src={src} alt={alt} />
      </button>
    </div>
  );
};

GenericTile.defaultProps = {
  id: "",
  src: "",
  title: "",
  generalStyles: {
    button: "",
    buttonActive: "",
    svgWrapper: ""
  },
  specificStyles: {
    button: "",
    buttonActive: "",
    svgWrapper: ""
  },
  isActive: false,
  tooltipContent: [],
  areHoverStylesEnabled: true,
  onClick: () => {}
};

export default GenericTile;
